import { useCallback, useEffect, useRef } from 'react';

import { useSiteConfig } from 'hooks/useSiteConfig';
import { SUPPORTED_LOCALE } from 'utils/constants';

export const EmplifiScript = ({
  sku,
  pageId,
}:
  | {
      sku: string;
      pageId: 'pdp-page';
    }
  | {
      sku?: never;
      pageId: 'email-landing-page' | 'order-confirmation-page';
    }) => {
  const prevSku = useRef(sku);
  const { locale, env } = useSiteConfig();
  const emplifiTurnToSite =
    env.emplifiTurnToSite && JSON.parse(env.emplifiTurnToSite);

  const addEmplifiWidgetWithConfig = useCallback(async () => {
    const idScript1 = 'emplifi-and-turnToConfig';
    const idScript2 = 'turnTo-widgets-script';

    document.getElementById(idScript1)?.remove();
    document.getElementById(idScript2)?.remove();

    const { turnToSiteKey, siteLocale } = getTurnToSiteKeyAndLocale(
      locale,
      emplifiTurnToSite
    );

    await addScript({
      id: idScript1,
      text:
        (sku
          ? `var turnToConfig = {
                sku: "${sku}",
                locale: "${siteLocale}",
                pageId: "${pageId}"
              };
            `
          : `var turnToConfig = {
                locale: "${siteLocale}",
                pageId: "${pageId}"
              };
            `) +
        'window.TurnToCmd=window.TurnToCmd||function(){(TurnToCmd.q=TurnToCmd.q||[]).push(arguments)};',
    });

    await addScript({
      id: idScript2,
      url:
        `https://widgets.turnto.com/v5/widgets/${turnToSiteKey}/js/turnto.js` +
        // hack https://stackoverflow.com/questions/9642205/how-to-force-a-script-reload-and-re-execute#comment12241680_9642359
        '?cachebuster=' +
        new Date().getTime(),
    });
  }, [emplifiTurnToSite, locale, pageId, sku]);

  useEffect(() => {
    addEmplifiWidgetWithConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (prevSku.current !== sku) addEmplifiWidgetWithConfig();
    prevSku.current = sku;
  }, [addEmplifiWidgetWithConfig, sku]);

  return null;
};

// ── UTILS ONLY FOR THIS FILE ───────────────────────────────────────────────────────────────────────────

const addScript = async ({
  text,
  url,
  id,
}: {
  text?: string;
  url?: string;
  id: string;
}) => {
  return new Promise((resolve, reject) => {
    try {
      const newScript = document.createElement('script');
      newScript.id = id;
      if (text) {
        newScript.text = text;
        resolve('');
      }
      if (url) {
        newScript.src = url;
        newScript.addEventListener('load', resolve);
      }
      newScript.addEventListener('error', reject);
      document.body.append(newScript);
    } catch (e) {
      reject(e);
    }
  });
};

const getTurnToSiteKeyAndLocale = (
  locale: string,
  emplifiTurnToSite: any
): {
  turnToSiteKey: string;
  siteLocale: string;
} => {
  switch (locale) {
    case SUPPORTED_LOCALE.EN_CA:
      return {
        turnToSiteKey: emplifiTurnToSite.en_CA.turnToSiteKey,
        siteLocale: emplifiTurnToSite.en_CA.siteLocale,
      };
    case SUPPORTED_LOCALE.FR_CA:
      return {
        turnToSiteKey: emplifiTurnToSite.fr_CA.turnToSiteKey,
        siteLocale: emplifiTurnToSite.fr_CA.siteLocale,
      };
    case SUPPORTED_LOCALE.EN_US:
    default:
      return {
        turnToSiteKey: emplifiTurnToSite.turnToSiteKey,
        siteLocale: emplifiTurnToSite.siteLocale,
      };
  }
};
